import * as Blockly from 'blockly';
import { FieldVariableActionsBase, registerVariableActionHandler, VariableActionRequest } from '@shared/blockly/fields';

export { registerVariableActionHandler, VariableActionRequest };

/** Variable selection with a separate footer for variable management. */
export class FieldVariableActions extends FieldVariableActionsBase {
  protected override showEditor_(event?: MouseEvent): void {
    // Keep Blockly's selection, checkmark, pointer handling and arrow-key navigation.
    super.showEditor_(event);
    const menu = this.menu_;
    const menuElement = menu?.getElement();
    if (!menu || !menuElement) return;

    const content = Blockly.DropDownDiv.getContentDiv();
    content.parentElement?.classList.add('blocklyVariableDropdown');
    content.style.height = '';
    Blockly.DropDownDiv.setColour('var(--bs-body-bg, #fff)', 'var(--bs-border-color, #dee2e6)');

    const footer = document.createElement('div');
    footer.className = 'blocklyVariableDropdownActions';
    const buttons: HTMLButtonElement[] = [];
    // Respect Blockly's available actions, including configurations that disable deletion.
    for (const option of this.getVariableOptionsIncludingActions(true)) {
      if (option === Blockly.FieldDropdown.SEPARATOR) continue;
      const id = option[1];
      if (id !== Blockly.RENAME_VARIABLE_ID && id !== Blockly.DELETE_VARIABLE_ID) continue;

      const deleting = id === Blockly.DELETE_VARIABLE_ID;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `btn btn-sm ${deleting ? 'btn-outline-danger' : 'btn-outline-secondary'}`;
      const icon = document.createElement('i');
      icon.className = deleting ? 'ri-delete-bin-line' : 'ri-pencil-line';
      icon.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = deleting
        ? $localize`:@@blockly-variable-delete-named:Variable „${this.getText()}:VARIABLE_NAME:“ löschen`
        : $localize`:@@blockly-variable-rename:Umbenennen …`;
      button.append(icon, label);
      button.addEventListener('click', e => {
        e.stopPropagation();
        const block = this.getSourceBlock();
        const variable = this.getVariable();
        Blockly.DropDownDiv.hideIfOwner(this, true);
        if (!block || block.isDeadOrDying() || !variable) return;
        this.requestVariableAction(deleting ? 'delete' : 'rename');
      });
      footer.appendChild(button);
      buttons.push(button);
    }
    content.appendChild(footer);
    // Native button keys must not bubble into Blockly's workspace shortcuts.
    footer.addEventListener('keydown', e => e.stopPropagation());

    // The list is one tab stop; the native buttons follow it. Escape restores field focus.
    content.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        Blockly.DropDownDiv.hideIfOwner(this, true);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        const stops: HTMLElement[] = [menuElement, ...buttons];
        const current = stops.indexOf(document.activeElement as HTMLElement);
        const next = (current + (e.shiftKey ? -1 : 1) + stops.length) % stops.length;
        stops[next]?.focus({ preventScroll: true });
      }
    }, { capture: true });

    // Include the footer in positioning, especially near workspace edges.
    Blockly.DropDownDiv.repositionForWindowResize();
  }
}
